class VideoTestimonials extends HTMLElement {
  constructor() {
    super();
    this.carousel = null;
    this.carouselSwiper = null;
    this.lightbox = null;
    this.lightboxSwiper = null;
    this.activeVideo = null;
    this.previousFocus = null;
    this.resizeObserver = null;
    this.lightboxOriginalParent = null;
    this.lightboxOriginalNextSibling = null;
    this.scrollY = 0;
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.carousel = this.querySelector('[data-video-testimonials-carousel]');
    this.lightbox = this.querySelector('[data-video-testimonials-lightbox]');
    this.initCarouselWhenReady();

    this.querySelectorAll('[data-video-testimonial-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.videoTestimonialIndex || 0);
        this.open(index);
      });
    });

    this.querySelectorAll('[data-video-testimonial-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.querySelector('[data-video-testimonial-prev]')?.addEventListener('click', () => {
      this.lightboxSwiper?.slidePrev();
    });

    this.querySelector('[data-video-testimonial-next]')?.addEventListener('click', () => {
      this.lightboxSwiper?.slideNext();
    });

    this.querySelector('[data-video-testimonial-mute]')?.addEventListener('click', () => {
      if (!this.activeVideo) return;
      this.activeVideo.muted = !this.activeVideo.muted;
      this.updateMuteLabel();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.resizeObserver?.disconnect();
    this.carouselSwiper?.destroy(true, true);
    this.lightboxSwiper?.destroy(true, true);
    this.unlockScroll();
    this.restoreLightboxLocation();
    this.carouselSwiper = null;
    this.lightboxSwiper = null;
  }

  initCarouselWhenReady(attempt = 0) {
    if (!this.carousel || this.carouselSwiper) return;

    const swiperElement = this.carousel.querySelector('.swiper');
    if (!swiperElement || !window.Swiper) {
      if (attempt < 20) {
        window.requestAnimationFrame(() => this.initCarouselWhenReady(attempt + 1));
      }
      return;
    }

    const width = this.getCarouselWidth();
    if ((!Number.isFinite(width) || width < 20) && attempt < 20) {
      window.requestAnimationFrame(() => this.initCarouselWhenReady(attempt + 1));
      return;
    }

    const getNumber = (name, fallback) => {
      const value = Number.parseFloat(this.carousel.dataset[name]);
      return Number.isFinite(value) && value > 0 ? value : fallback;
    };

    const getBoolean = (name) => this.carousel.dataset[name] === 'true';
    const gap = getNumber('gap', 16);

    this.carouselSwiper = new Swiper(swiperElement, {
      width,
      slidesPerView: getNumber('mobileSlides', 1.2),
      spaceBetween: gap,
      loop: getBoolean('loop'),
      watchSlidesProgress: true,
      speed: 500,
      autoplay: {
        enabled: getBoolean('autoplay'),
        delay: getNumber('autoplaySpeed', 4) * 1000,
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
      },
      navigation: {
        nextEl: this.carousel.querySelector('.swiper-button-next'),
        prevEl: this.carousel.querySelector('.swiper-button-prev'),
        disabledClass: 'swiper-button-disabled',
        hiddenClass: 'swiper-button-hidden',
      },
      pagination: {
        el: this.carousel.querySelector('.swiper-pagination'),
        clickable: true,
      },
      breakpoints: {
        750: {
          slidesPerView: getNumber('tabletSlides', 2),
          spaceBetween: gap,
        },
        990: {
          slidesPerView: getNumber('desktopSlides', 3),
          spaceBetween: gap,
        },
      },
    });

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.carouselSwiper) return;
      window.requestAnimationFrame(() => {
        if (!this.carouselSwiper) return;
        const width = this.getCarouselWidth();
        if (!width) return;
        this.carouselSwiper.params.width = width;
        this.carouselSwiper.update();
      });
    });
    this.resizeObserver.observe(this.carousel);
  }

  getCarouselWidth() {
    if (!this.carousel) return 0;

    const rectWidth = this.carousel.getBoundingClientRect().width;
    const parentWidth = this.carousel.parentElement?.getBoundingClientRect().width || rectWidth;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const boundedWidth = Math.min(rectWidth, parentWidth, viewportWidth);

    return Number.isFinite(boundedWidth) && boundedWidth > 0 ? boundedWidth : 0;
  }

  open(index = 0) {
    if (!this.lightbox) return;

    this.previousFocus = document.activeElement;
    this.moveLightboxToBody();
    this.lightbox.hidden = false;
    this.lockScroll();
    document.addEventListener('keydown', this.handleKeydown);

    this.initLightboxSwiper(index);
    this.lightbox.querySelector('[data-video-testimonial-close]')?.focus();
  }

  close() {
    if (!this.lightbox) return;

    this.pauseAllVideos();
    this.lightbox.hidden = true;
    this.unlockScroll();
    this.restoreLightboxLocation();
    document.removeEventListener('keydown', this.handleKeydown);

    if (this.previousFocus instanceof HTMLElement) {
      this.previousFocus.focus();
    }
  }

  initLightboxSwiper(index) {
    if (!this.lightbox) return;

    const swiperElement = this.lightbox.querySelector('.video-testimonials-lightbox__swiper');
    if (!swiperElement) return;

    if (!this.lightboxSwiper && window.Swiper) {
      this.lightboxSwiper = new Swiper(swiperElement, {
        slidesPerView: 1,
        speed: 350,
        loop: false,
        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
        on: {
          init: () => this.playActiveVideo(),
          slideChange: () => this.playActiveVideo(),
        },
      });
    }

    if (this.lightboxSwiper) {
      this.lightboxSwiper.slideTo(index, 0);
      this.playActiveVideo();
    } else {
      this.playActiveVideo();
    }
  }

  moveLightboxToBody() {
    if (!this.lightbox || this.lightbox.parentElement === document.body) return;

    this.lightboxOriginalParent = this.lightbox.parentElement;
    this.lightboxOriginalNextSibling = this.lightbox.nextSibling;
    document.body.appendChild(this.lightbox);
  }

  restoreLightboxLocation() {
    if (!this.lightbox || !this.lightboxOriginalParent || this.lightbox.parentElement !== document.body) return;

    this.lightboxOriginalParent.insertBefore(this.lightbox, this.lightboxOriginalNextSibling);
  }

  lockScroll() {
    this.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add('video-testimonials-lightbox-open');
    document.body.classList.add('video-testimonials-lightbox-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!document.body.classList.contains('video-testimonials-lightbox-open')) return;

    document.documentElement.classList.remove('video-testimonials-lightbox-open');
    document.body.classList.remove('video-testimonials-lightbox-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this.scrollY || 0);
  }

  playActiveVideo() {
    this.pauseAllVideos();

    const activeSlide = this.lightboxSwiper?.slides?.[this.lightboxSwiper.activeIndex] ||
      this.lightbox?.querySelector('.video-testimonials-lightbox__slide');
    const video = activeSlide?.querySelector('video');

    if (!(video instanceof HTMLVideoElement)) return;

    this.activeVideo = video;
    video.muted = false;
    video.controls = false;
    video.onended = () => this.handleVideoEnded();
    video.ontimeupdate = () => this.updateProgress();
    video.onloadedmetadata = () => this.updateProgress();
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => {});
      this.updateMuteLabel();
    });

    this.updateProgress();
    this.updateMuteLabel();
  }

  pauseAllVideos() {
    this.lightbox?.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.currentTime = 0;
      video.onended = null;
      video.ontimeupdate = null;
      video.onloadedmetadata = null;
    });
    this.activeVideo = null;
    this.updateProgress();
  }

  handleVideoEnded() {
    if (!this.lightboxSwiper) return;

    if (this.lightboxSwiper.activeIndex < this.lightboxSwiper.slides.length - 1) {
      this.lightboxSwiper.slideNext();
    } else {
      this.lightboxSwiper.slideTo(0);
    }
  }

  updateProgress() {
    const root = this.lightbox || this;
    const progress = root.querySelector('[data-video-testimonial-progress]');
    const time = root.querySelector('[data-video-testimonial-time]');

    if (!this.activeVideo || !Number.isFinite(this.activeVideo.duration) || this.activeVideo.duration === 0) {
      if (progress) progress.style.width = '0%';
      if (time) time.textContent = '0:00';
      return;
    }

    const percent = (this.activeVideo.currentTime / this.activeVideo.duration) * 100;
    if (progress) progress.style.width = `${percent}%`;
    if (time) time.textContent = this.formatTime(this.activeVideo.currentTime);
  }

  updateMuteLabel() {
    const root = this.lightbox || this;
    const label = root.querySelector('[data-video-testimonial-mute-label]');
    const button = root.querySelector('[data-video-testimonial-mute]');
    if (!label || !button || !this.activeVideo) return;

    label.textContent = this.activeVideo.muted ? 'Unmute' : 'Mute';
    button.setAttribute('aria-label', this.activeVideo.muted ? 'Unmute video' : 'Mute video');
  }

  formatTime(value) {
    const totalSeconds = Math.floor(value || 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.lightboxSwiper?.slidePrev();
    } else if (event.key === 'ArrowRight') {
      this.lightboxSwiper?.slideNext();
    }
  }
}

if (!customElements.get('video-testimonials')) {
  customElements.define('video-testimonials', VideoTestimonials);
}
